import api from '@/lib/axios';

export interface StoreProduct {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  imageStorageKey?: string;
  pointPrice: string;
  costBrl?: string | null;
  availableQty: number;
  maxPerUser?: number;
  active: boolean;
  canBuy?: boolean;
  alreadyBoughtInCurrentBatch?: boolean;
  purchaseCount?: number;
  userPoints?: number;
  activeGroupPurchases?: GroupPurchaseSummary[];
  createdAt: string;
  updatedAt: string;
}

export interface GroupPurchaseSummary {
  id: string;
  productId: string;
  targetPoints: string;
  collectedPoints: string;
  participantCount: number;
  status: string;
  createdByUserId: string;
  createdByEmail?: string;
  productName?: string;
  expiresAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface GroupPurchaseParticipant {
  userId: string;
  email?: string;
  points: string;
}

export interface GroupPurchaseDetail extends GroupPurchaseSummary {
  tenantId: string;
  createdByUserId: string;
  purchaseId?: string;
  product?: StoreProduct;
  contributions: Array<{
    userId: string;
    email?: string;
    pointsAmount: string;
    createdAt: string;
  }>;
  participants: GroupPurchaseParticipant[];
  userContribution: number;
  remainingPoints: number;
}

export interface XpTransaction {
  id: string;
  userId: string;
  amount: string;
  reason: string;
  taskId?: string;
  taskType?: string;
  eventId?: string;
  baseAmount?: string;
  multiplier: string;
  bonusAmount: string;
  description?: string;
  createdAt: string;
}

export interface XpTransactionPage {
  items: XpTransaction[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface StorePurchaseItem {
  id: string;
  productId: string;
  productName: string;
  imageUrl?: string;
  pointPrice: string;
  pointsSpent: string;
  purchaseType: 'individual' | 'group';
  participantCount?: number;
  userContribution?: string | null;
  participants?: GroupPurchaseParticipant[];
  createdAt: string;
}

export interface AdminStorePurchaseItem extends StorePurchaseItem {
  userId: string;
  userEmail?: string;
}

export interface StorePurchasesPage {
  items: StorePurchaseItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AdminStorePurchasesPage {
  items: AdminStorePurchaseItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface StoreSettings {
  pointsPerBrl: number | null;
  updatedAt: string | null;
}

export interface SuggestedRate {
  periodDays: number;
  userCount: number;
  teamPointsPerMonth: number;
  maxTeamPointsPerMonth: number;
  safetyFactor: number;
  monthlyBudgetBrl: number;
  suggestedPointsPerBrl: number | null;
}

export interface StoreNotificationSettings {
  userIds: string[];
  availableUsers: Array<{
    id: string;
    email: string;
    fullName: string | null;
  }>;
}

export interface GlobalEvent {
  id: string;
  tenantId: string;
  title: string;
  description?: string;
  emoji?: string;
  xpMultiplier: string;
  active: boolean;
  startsAt?: string;
  endsAt?: string;
  dismissedToday?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FixedObjective {
  id: string;
  tenantId: string;
  key: string;
  name: string;
  description?: string;
  emoji?: string;
  config?: Record<string, unknown>;
  active: boolean;
  updatedAt: string;
}

export interface UserStreak {
  currentStreak: number;
  longestStreak: number;
  lastActiveDate?: string;
}

export interface AdminInsights {
  usersWithPoints: Array<{
    userId: string;
    email: string;
    points: string;
    level: number;
  }>;
  totalPointsInCirculation: number;
  dailyMaxByType: Record<string, { points: number; dailyLimit: number; maxDaily: number }>;
  totalMaxDaily: number;
  totalActiveProducts: number;
  totalPurchases: number;
}

export const storeService = {
  // User
  getProducts: async (): Promise<StoreProduct[]> => {
    const { data } = await api.get<StoreProduct[]>('/store/products');
    return data;
  },

  buyProduct: async (productId: string) => {
    const { data } = await api.post(`/store/products/${productId}/buy`);
    return data;
  },

  createGroupPurchase: async (productId: string, initialContribution?: number) => {
    const { data } = await api.post<GroupPurchaseDetail>('/store/group-purchases', {
      productId,
      initialContribution,
    });
    return data;
  },

  listGroupPurchases: async (productId?: string, status = 'collecting') => {
    const { data } = await api.get<GroupPurchaseSummary[]>('/store/group-purchases', {
      params: { productId, status },
    });
    return data;
  },

  getGroupPurchase: async (groupPurchaseId: string) => {
    const { data } = await api.get<GroupPurchaseDetail>(
      `/store/group-purchases/${groupPurchaseId}`,
    );
    return data;
  },

  contributeGroupPurchase: async (groupPurchaseId: string, pointsAmount: number) => {
    const { data } = await api.post(`/store/group-purchases/${groupPurchaseId}/contribute`, {
      pointsAmount,
    });
    return data;
  },

  cancelGroupPurchase: async (groupPurchaseId: string) => {
    const { data } = await api.post(`/store/group-purchases/${groupPurchaseId}/cancel`);
    return data;
  },

  getPointsHistory: async (page = 1, limit = 20): Promise<XpTransactionPage> => {
    const { data } = await api.get<XpTransactionPage>('/store/points-history', {
      params: { page, limit },
    });
    return data;
  },

  getMyPurchases: async (page = 1, limit = 20): Promise<StorePurchasesPage> => {
    const { data } = await api.get<StorePurchasesPage>('/store/my-purchases', {
      params: { page, limit },
    });
    return data;
  },

  // Admin
  adminGetProducts: async (): Promise<StoreProduct[]> => {
    const { data } = await api.get<StoreProduct[]>('/store/admin/products');
    return data;
  },

  createProduct: async (dto: {
    name: string;
    description?: string;
    pointPrice: number;
    costBrl?: number;
    availableQty?: number;
    maxPerUser?: number;
    active?: boolean;
  }): Promise<StoreProduct> => {
    const { data } = await api.post<StoreProduct>('/store/admin/products', dto);
    return data;
  },

  updateProduct: async (
    productId: string,
    dto: Partial<{
      name: string;
      description: string;
      pointPrice: number;
      costBrl: number;
      availableQty: number;
      maxPerUser: number | null;
      active: boolean;
    }>,
  ): Promise<StoreProduct> => {
    const { data } = await api.patch<StoreProduct>(`/store/admin/products/${productId}`, dto);
    return data;
  },

  deleteProduct: async (productId: string) => {
    const { data } = await api.delete(`/store/admin/products/${productId}`);
    return data;
  },

  addStock: async (productId: string, qty: number, note?: string) => {
    const { data } = await api.post(`/store/admin/products/${productId}/stock`, { qty, note });
    return data;
  },

  getProductHistory: async (productId: string) => {
    const { data } = await api.get(`/store/admin/products/${productId}/history`);
    return data;
  },

  uploadProductImage: async (productId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(`/store/admin/products/${productId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  getAdminInsights: async (): Promise<AdminInsights> => {
    const { data } = await api.get<AdminInsights>('/store/admin/insights');
    return data;
  },

  adminGetAllPurchases: async (page = 1, limit = 20): Promise<AdminStorePurchasesPage> => {
    const { data } = await api.get<AdminStorePurchasesPage>('/store/admin/purchases', {
      params: { page, limit },
    });
    return data;
  },

  getStoreSettings: async (): Promise<StoreSettings> => {
    const { data } = await api.get<StoreSettings>('/store/admin/settings');
    return data;
  },

  updateStoreSettings: async (pointsPerBrl: number): Promise<StoreSettings> => {
    const { data } = await api.put<StoreSettings>('/store/admin/settings', { pointsPerBrl });
    return data;
  },

  getSuggestedRate: async (
    monthlyBudgetBrl: number,
    safetyFactor?: number,
  ): Promise<SuggestedRate> => {
    const { data } = await api.get<SuggestedRate>('/store/admin/settings/suggested-rate', {
      params: { monthlyBudgetBrl, safetyFactor },
    });
    return data;
  },

  getNotificationSettings: async (): Promise<StoreNotificationSettings> => {
    const { data } = await api.get<StoreNotificationSettings>(
      '/store/admin/notification-settings',
    );
    return data;
  },

  updateNotificationSettings: async (userIds: string[]): Promise<StoreNotificationSettings> => {
    const { data } = await api.put<StoreNotificationSettings>(
      '/store/admin/notification-settings',
      { userIds },
    );
    return data;
  },

  // Events
  getActiveEvents: async (): Promise<GlobalEvent[]> => {
    const { data } = await api.get<GlobalEvent[]>('/gamification/events/active');
    return data;
  },

  dismissEvent: async (eventId: string) => {
    const { data } = await api.post(`/gamification/events/${eventId}/dismiss`);
    return data;
  },

  adminGetEvents: async (): Promise<GlobalEvent[]> => {
    const { data } = await api.get<GlobalEvent[]>('/gamification/admin/events');
    return data;
  },

  adminCreateEvent: async (dto: Partial<GlobalEvent>): Promise<GlobalEvent> => {
    const { data } = await api.post<GlobalEvent>('/gamification/admin/events', dto);
    return data;
  },

  adminUpdateEvent: async (eventId: string, dto: Partial<GlobalEvent>): Promise<GlobalEvent> => {
    const { data } = await api.patch<GlobalEvent>(`/gamification/admin/events/${eventId}`, dto);
    return data;
  },

  adminDeleteEvent: async (eventId: string) => {
    const { data } = await api.delete(`/gamification/admin/events/${eventId}`);
    return data;
  },

  // Fixed objectives
  getFixedObjectives: async (): Promise<FixedObjective[]> => {
    const { data } = await api.get<FixedObjective[]>('/gamification/admin/fixed-objectives');
    return data;
  },

  updateFixedObjective: async (
    objectiveId: string,
    dto: Partial<FixedObjective>,
  ): Promise<FixedObjective> => {
    const { data } = await api.patch<FixedObjective>(
      `/gamification/admin/fixed-objectives/${objectiveId}`,
      dto,
    );
    return data;
  },

  // Streak
  getStreak: async (): Promise<UserStreak> => {
    const { data } = await api.get<UserStreak>('/gamification/streak');
    return data;
  },
};
